/**
 * Maps lucide-style icon string names (from database) to Phosphor icon components.
 * Used by automation templates and anywhere icon strings need to be resolved to React components.
 */
import React from 'react';
import {
  ArrowsClockwise,
  ArrowSquareOut,
  Bell,
  BookOpen,
  Brain,
  Briefcase,
  Buildings,
  Calculator,
  Calendar,
  CaretDown,
  ChartBar,
  ChartLine,
  ChatCircle,
  ChatText,
  Check,
  CheckCircle,
  CheckSquare,
  CircleNotch,
  Clipboard,
  Clock,
  ClockCounterClockwise,
  Code,
  Copy,
  CreditCard,
  Crown,
  CurrencyDollar,
  Database,
  DownloadSimple,
  Envelope,
  Eye,
  EyeSlash,
  FileCode,
  FileText,
  Files,
  Funnel,
  Flask,
  FlowArrow,
  FloppyDisk,
  Folder,
  GearSix,
  Gift,
  GitBranch,
  Globe,
  HardDrives,
  Headphones,
  House,
  Image,
  Info,
  Key,
  Lightning,
  Lightbulb,
  Link,
  LinkSimple,
  ListBullets,
  ListChecks,
  Lock,
  MagnifyingGlass,
  MapTrifold,
  Megaphone,
  Microphone,
  Minus,
  MusicNote,
  Package,
  PaperPlaneRight,
  PencilSimple,
  Phone,
  Play,
  Plug,
  Plus,
  PlusCircle,
  Question,
  Receipt,
  Robot,
  Rocket,
  ShareNetwork,
  Shield,
  ShoppingCart,
  Sparkle,
  SpeakerHigh,
  Star,
  Table,
  Tag,
  Target,
  Trash,
  Tray,
  TrendUp,
  Trophy,
  UploadSimple,
  User,
  UserCircle,
  UserGear,
  UsersThree,
  Video,
  Warning,
  WarningCircle,
  WebhooksLogo,
  Wrench,
  X,
  XCircle,
} from '@phosphor-icons/react';

const ICON_STRING_MAP = {
  'activity': Lightning,
  'alert-circle': WarningCircle,
  'alert-triangle': Warning,
  'award': Trophy,
  'bar-chart': ChartBar,
  'bar-chart-3': ChartBar,
  'bell': Bell,
  'book': BookOpen,
  'book-open': BookOpen,
  'bot': Robot,
  'brain': Brain,
  'briefcase': Briefcase,
  'building': Buildings,
  'building-2': Buildings,
  'calculator': Calculator,
  'calendar': Calendar,
  'check': Check,
  'check-circle': CheckCircle,
  'check-circle-2': CheckCircle,
  'check-square': CheckSquare,
  'clipboard': Clipboard,
  'clock': Clock,
  'code': Code,
  'copy': Copy,
  'credit-card': CreditCard,
  'crown': Crown,
  'database': Database,
  'dollar-sign': CurrencyDollar,
  'download': DownloadSimple,
  'edit': PencilSimple,
  'edit-2': PencilSimple,
  'edit-3': PencilSimple,
  'external-link': ArrowSquareOut,
  'eye': Eye,
  'eye-off': EyeSlash,
  'file': FileText,
  'file-code': FileCode,
  'file-text': FileText,
  'files': Files,
  'filter': Funnel,
  'flask': Flask,
  'folder': Folder,
  'gift': Gift,
  'git-branch': GitBranch,
  'globe': Globe,
  'headphones': Headphones,
  'help-circle': Question,
  'history': ClockCounterClockwise,
  'home': House,
  'image': Image,
  'inbox': Tray,
  'info': Info,
  'key': Key,
  'layers': Lightning,
  'lightbulb': Lightbulb,
  'link': Link,
  'link-2': LinkSimple,
  'list': ListBullets,
  'loader-2': CircleNotch,
  'lock': Lock,
  'mail': Envelope,
  'map': MapTrifold,
  'megaphone': Megaphone,
  'message-square': ChatText,
  'mic': Microphone,
  'minus': Minus,
  'music': MusicNote,
  'package': Package,
  'pencil': PencilSimple,
  'phone': Phone,
  'play': Play,
  'plug': Plug,
  'plus': Plus,
  'plus-circle': PlusCircle,
  'receipt': Receipt,
  'refresh-cw': ArrowsClockwise,
  'rocket': Rocket,
  'save': FloppyDisk,
  'search': MagnifyingGlass,
  'send': PaperPlaneRight,
  'server': HardDrives,
  'settings': GearSix,
  'share-2': ShareNetwork,
  'shield': Shield,
  'shopping-cart': ShoppingCart,
  'sparkles': Sparkle,
  'star': Star,
  'table': Table,
  'tag': Tag,
  'target': Target,
  'trash-2': Trash,
  'trending-up': TrendUp,
  'upload': UploadSimple,
  'user': User,
  'user-circle': UserCircle,
  'users': UsersThree,
  'video': Video,
  'volume-2': SpeakerHigh,
  'wand-2': Sparkle,
  'webhook': WebhooksLogo,
  'workflow': FlowArrow,
  'wrench': Wrench,
  'x': X,
  'x-circle': XCircle,
  'zap': Lightning,
  'message-circle': ChatCircle,
  'bar-chart-2': ChartBar,
  'bar-chart-3': ChartBar,
  'line-chart': ChartLine,
  'alert-triangle': Warning,
};

/**
 * Resolves an icon string (e.g., "message-square", "mail") to a Phosphor React component.
 * Returns null if the icon is an emoji or unknown string.
 */
export function resolveIconString(iconStr) {
  if (!iconStr || typeof iconStr !== 'string') return null;
  if (/[^\x00-\x7F]/.test(iconStr)) return null;
  return ICON_STRING_MAP[iconStr.toLowerCase()] || null;
}

/**
 * Renders a resolved Phosphor icon from a DB icon string.
 * Falls back to Robot icon if unresolvable.
 */
export function TemplateIcon({ icon, size = 20, className = '' }) {
  const IconComp = resolveIconString(icon);
  if (IconComp) return <IconComp size={size} weight="duotone" className={className} />;
  return <Robot size={size} weight="duotone" className={className} />;
}

export default ICON_STRING_MAP;
